import { Component, computed, effect, ElementRef, linkedSignal, signal, viewChild } from '@angular/core';
import { containDropdownWheel } from '../../../utils/dropdown-wheel.util';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Combobox, ComboboxPopup, ComboboxWidget } from '@angular/aria/combobox';
import { Listbox, Option } from '@angular/aria/listbox';
import { TranslocoDirective } from '@jsverse/transloco';
import { MatIcon } from '@angular/material/icon';
import { getCountriesInfo } from '@dss-libs/shared/utility/common-utilities';
import { ICountryInfo } from '@dss-libs/shared/type/common-types';
import { FormValueClassAbstract } from '../../../abstract/form-value-class-abstract';
import { JunoError } from '../juno-error/juno-error';

/**
 * A styled, accessible country-code picker built on {@link FormValueClassAbstract},
 * `@angular/aria` combobox/listbox primitives, and integrated with Angular Signal Forms.
 *
 * The trigger IS the combobox `<input>` (the same pattern as {@link JunoAutocomplete}):
 * the user types to filter countries and the Combobox directive owns the full ARIA
 * combobox contract plus keyboard handling (Escape, ArrowUp/Down, Enter). This avoids
 * the focus-model conflict that arises when a separate search field steals focus from
 * the combobox — which would make the popup close the instant it opened.
 *
 * Two signals carry different concerns:
 * - `filterText` — what the `<input>` shows: the selected country code when closed,
 *   or the live filter query while the user types.
 * - `value` (from the abstract) — the selected country's ISO alpha-2 code, surfaced to
 *   Signal Forms.
 *
 * The selected country's flag is overlaid at the start of the input and a chevron at
 * the end; the input padding leaves room for both.
 *
 * ---
 *
 * ### Inputs (own)
 *
 * None.
 *
 * ### Inputs (inherited from {@link FormValueClassAbstract})
 *
 * `value` (country code string, e.g. `'EG'`), `label`, `labelClass`, `placeholder`,
 * `name`, `appliedClasses`, `required`, `errors`, `pending`, `touched`, `dirty`,
 * `disabled`.
 *
 * ### Outputs (inherited)
 *
 * `touch` – emitted when the dropdown closes.
 *
 * ---
 *
 * @usageNotes
 *
 * #### With Signal Forms (`[formField]`)
 * ```html
 * <juno-libs-ui-phone-number-country-picker [formField]="profileForm.countryCode" label="COUNTRY" />
 * ```
 *
 * #### Standalone two-way binding (`[(value)]`)
 * ```html
 * <juno-libs-ui-phone-number-country-picker [(value)]="selectedCountry" />
 * ```
 *
 * @see {@link FormValueClassAbstract} for the full list of inherited inputs/outputs.
 * @see {@link JunoPhoneNumber} for the composite phone-number field that embeds this picker.
 */
@Component({
  selector: 'juno-libs-ui-phone-number-country-picker',
  imports: [
    TranslocoDirective,
    CdkConnectedOverlay,
    Combobox,
    ComboboxPopup,
    ComboboxWidget,
    Listbox,
    Option,
    MatIcon,
    JunoError,
  ],
  templateUrl: './juno-phone-number-country-picker.html',
  styleUrl: './juno-phone-number-country-picker.css',
})
export class JunoPhoneNumberCountryPicker extends FormValueClassAbstract<string, string> {
  protected readonly onDropdownWheel = containDropdownWheel;

  readonly allCountries = signal(getCountriesInfo()).asReadonly();
  readonly comboboxInputRef = viewChild<ElementRef<HTMLInputElement>>('comboboxInput');

  protected readonly popupExpanded = signal(false);
  protected filterText = signal('');

  protected readonly selectedCountry = computed(
    () => this.allCountries().find((c) => c.code === this.value()) ?? null,
    { equal: (a: ICountryInfo | null, b: ICountryInfo | null) => a?.code === b?.code }
  );

  protected readonly filteredCountries = computed<ICountryInfo[]>(() => {
    const query = this.filterText().trim().toLowerCase().replace('+', '');
    if (!query) return this.allCountries();
    return this.allCountries().filter(
      (country) =>
        country.code.toLowerCase().startsWith(query) ||
        country.name.toLowerCase().startsWith(query) ||
        country.isdCode.startsWith(query)
    );
  });

  protected readonly listboxValue = linkedSignal<string[]>(() => {
    const value = this.value();
    return value ? [value] : [];
  });

  readonly syncSelectedCode = effect(() => {
    if (!this.popupExpanded()) {
      this.filterText.set(this.selectedCountry()?.code ?? '');
    }
  });

  protected openDropdown(): void {
    if (this.disabled() || this.pending() || this.popupExpanded()) return;
    this.filterText.set('');
    this.popupExpanded.set(true);
  }

  protected onExpandedChange(isExpanded: boolean): void {
    if (isExpanded && (this.disabled() || this.pending())) return;
    this.popupExpanded.set(isExpanded);
    if (isExpanded) {
      this.filterText.set('');
    } else {
      this.filterText.set(this.selectedCountry()?.code ?? '');
      this.touch.emit();
    }
  }

  protected onListboxValueChange(values: string[]): void {
    const [selected] = values;
    if (!selected) return;
    this.value.set(selected);
    this.onExpandedChange(false);
  }

  focus(options?: FocusOptions): void {
    this.comboboxInputRef()?.nativeElement.focus(options);
  }
}
